import { useEffect, useMemo, useState } from "react";
import { Copy, CalendarPlus, Share2, MapPin, Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Real couple photos — hosted locally on backend for iOS Safari compatibility (avoids customer-assets CORS/ITP issues)
const COUPLE_PHOTO_SEATED = "/api/static/couple_seated.jpeg";
const COUPLE_KISS_DOOR = "/api/static/couple_kiss.jpeg";
const COUPLE_PHOTO = "/api/static/couple_dallas.jpeg";

// AI-generated Tuscan / Italian Mediterranean atmosphere imagery (served from backend)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TUSCAN_HERO = `${BACKEND_URL}/api/static/tuscan_hero.png`;
// D'Vine Grace real venue photos (McKinney, TX) — the actual wedding venue
const RING_HAND = `${BACKEND_URL}/api/static/ring_hand.png`;
const BL_LOGO = `${BACKEND_URL}/api/static/bl_logo.png`;
const DALLAS_BG = `${BACKEND_URL}/api/static/dallas_skyline_bg.png`;
const VENUE_OUTDOOR = `${BACKEND_URL}/api/static/venue_Outdoor-Wedding.jpg`;
const VENUE_CHAPEL = `${BACKEND_URL}/api/static/venue_Chapel-Wedding-2.jpg`;
const VENUE_TABLE = `${BACKEND_URL}/api/static/venue_Table-Decor-2.jpg`;
const VENUE_PIAZZA = `${BACKEND_URL}/api/static/venue_wedding-venue-with-catering-included-mckinney-texas.jpg`;
const VENUE_GARDEN_ARCH = `${BACKEND_URL}/api/static/venue_Benvenuti.jpg`;

// Event details
const EVENT = {
  bride: "Bricia",
  groom: "Luis",
  // 05.21.2027 — 5pm local (placeholder time, only date is announced)
  dateISO: "2027-05-21",
  dateStart: "20270521T173000",
  dateEnd: "20270522T020000",
  prettyDate: "05.21.2027",
  longDate: "Viernes, 21 de Mayo de 2027",
  city: "McKinney, Texas",
  address: "McKinney, Texas (Dallas area)",
  phrase: "Te invitamos a celebrar nuestro amor",
};

// Bilingual texts ES/EN
const I18N = {
  es: {
    saveTheDate: "SAVE THE DATE",
    forWedding: "PARA LA BODA DE",
    month: "MAYO",
    cityShort: "MCKINNEY, TEXAS",
    cityArea: "(Dallas area)",
    phrase: "Te invitamos a celebrar nuestro amor",
    destinationLove: "DESTINATION LOVE",
    longDate: "Viernes, 21 de Mayo de 2027",
    countdownLabel: "Cuenta regresiva",
    units: { d: "Días", h: "Hrs", m: "Min", s: "Seg" },
    addToCal: "Agregar al calendario",
    copyLink: "Copiar link",
    share: "Compartir",
    toastCopied: "Link copiado",
    toastCopiedDesc: "Ya puedes compartirlo con tus invitados.",
    toastError: "No se pudo copiar el link",
    shareTitle: "Save the Date — Bricia y Luis",
  },
  en: {
    saveTheDate: "SAVE THE DATE",
    forWedding: "FOR THE WEDDING OF",
    month: "MAY",
    cityShort: "MCKINNEY, TEXAS",
    cityArea: "(Dallas area)",
    phrase: "Join us to celebrate our love",
    destinationLove: "DESTINATION LOVE",
    longDate: "Friday, May 21, 2027",
    countdownLabel: "Countdown",
    units: { d: "Days", h: "Hrs", m: "Min", s: "Sec" },
    addToCal: "Add to calendar",
    copyLink: "Copy link",
    share: "Share",
    toastCopied: "Link copied",
    toastCopiedDesc: "You can now share it with your guests.",
    toastError: "Could not copy the link",
    shareTitle: "Save the Date — Bricia & Luis",
  },
};

function useCountdown(targetDateISO) {
  const target = useMemo(() => new Date(`${targetDateISO}T17:30:00`), [
    targetDateISO,
  ]);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff / 3600000) % 24);
  const minutes = Math.floor((diff / 60000) % 60);
  const seconds = Math.floor((diff / 1000) % 60);
  return { days, hours, minutes, seconds };
}

function downloadICS() {
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Save the Date//Bricia y Luis//ES",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:bricia-luis-2027-save-the-date@savethedate`,
    `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`,
    `DTSTART;TZID=America/Chicago:${EVENT.dateStart}`,
    `DTEND;TZID=America/Chicago:${EVENT.dateEnd}`,
    "SUMMARY:Boda de Bricia y Luis",
    `DESCRIPTION:${EVENT.phrase}. Invitación formal a seguir.`,
    `LOCATION:${EVENT.address}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Bricia-y-Luis-Save-the-Date.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function googleCalUrl() {
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: "Boda de Bricia y Luis",
    dates: `${EVENT.dateStart}/${EVENT.dateEnd}`,
    ctz: "America/Chicago",
    details: `${EVENT.phrase}. Invitación formal a seguir.`,
    location: EVENT.address,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

function outlookCalUrl() {
  const startISO = "2027-05-21T17:30:00";
  const endISO = "2027-05-22T02:00:00";
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: "Boda de Bricia y Luis",
    startdt: startISO,
    enddt: endISO,
    body: `${EVENT.phrase}. Invitación formal a seguir.`,
    location: EVENT.address,
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

const OliveBranch = ({ className = "", flip = false }) => (
  <svg
    viewBox="0 0 120 40"
    className={className}
    style={{ transform: flip ? "scaleX(-1)" : "none" }}
    aria-hidden="true"
  >
    <path
      d="M5 22 C 35 22, 65 22, 115 22"
      stroke="currentColor"
      strokeWidth="1"
      fill="none"
    />
    <g fill="currentColor" opacity="0.85">
      <ellipse cx="20" cy="18" rx="6" ry="2.2" transform="rotate(-25 20 18)" />
      <ellipse cx="32" cy="26" rx="6" ry="2.2" transform="rotate(20 32 26)" />
      <ellipse cx="46" cy="17" rx="6.5" ry="2.4" transform="rotate(-22 46 17)" />
      <ellipse cx="60" cy="27" rx="6.5" ry="2.4" transform="rotate(22 60 27)" />
      <ellipse cx="74" cy="17" rx="6" ry="2.2" transform="rotate(-22 74 17)" />
      <ellipse cx="88" cy="27" rx="6" ry="2.2" transform="rotate(22 88 27)" />
      <ellipse cx="100" cy="19" rx="5" ry="2" transform="rotate(-22 100 19)" />
    </g>
  </svg>
);

// Family-crest B&L monogram — pill frame with italic B/L and two olive branches
const FamilyCrest = ({ className = "", color = "#5b6b48" }) => {
  // Leaf at origin (0,0): pointed almond ~24x8 with central vein
  const Leaf = ({ x, y, rot, sc = 1, veinColor = "#f3e9d6" }) => (
    <g transform={`translate(${x},${y}) rotate(${rot}) scale(${sc})`}>
      <path
        d="M -10 0 Q -2 -3.4 10 0 Q -2 3.4 -10 0 Z"
        fill={color}
        stroke={color}
        strokeWidth="0.3"
      />
      <path d="M -8 0 L 8 0" stroke={veinColor} strokeWidth="0.5" />
    </g>
  );

  // helper to render a list of leaves
  const leaves = (list) =>
    list.map(([x, y, rot, sc], i) => <Leaf key={i} x={x} y={y} rot={rot} sc={sc} />);

  return (
    <svg viewBox="0 0 480 360" className={className} aria-hidden="true">
      {/* Pill frame */}
      <rect
        x="195"
        y="60"
        width="90"
        height="150"
        rx="45"
        ry="45"
        fill="none"
        stroke={color}
        strokeWidth="2.2"
      />

      {/* B (italic serif, upper-left inside pill) */}
      <text
        x="225"
        y="120"
        textAnchor="middle"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontWeight="500"
        fontSize="68"
        fill={color}
      >
        B
      </text>
      {/* L (italic serif, lower-right inside pill) */}
      <text
        x="258"
        y="190"
        textAnchor="middle"
        fontFamily="Cormorant Garamond, serif"
        fontStyle="italic"
        fontWeight="500"
        fontSize="68"
        fill={color}
      >
        L
      </text>

      {/* LEFT olive branch - main stem */}
      <path
        d="M55 295 C 115 268, 175 240, 230 225"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      {/* LEFT leaves */}
      {leaves([
        [70, 287, -40, 1.0],
        [90, 277, -38, 1.05],
        [112, 266, -36, 1.1],
        [134, 255, -34, 1.1],
        [156, 245, -32, 1.05],
        [178, 236, -30, 1.0],
        [200, 230, -28, 0.95],
        [80, 297, 38, 1.0],
        [102, 287, 36, 1.05],
        [124, 276, 34, 1.1],
        [146, 265, 32, 1.05],
        [168, 256, 30, 1.0],
        [190, 248, 28, 0.95],
      ])}
      {/* LEFT berries near base */}
      <circle cx="60" cy="297" r="2.8" fill={color} />
      <circle cx="74" cy="291" r="2.8" fill={color} />
      <circle cx="90" cy="283" r="2.8" fill={color} />
      <circle cx="110" cy="272" r="2.8" fill={color} />

      {/* RIGHT olive branch - main stem (mirrored) */}
      <path
        d="M425 295 C 365 268, 305 240, 250 225"
        fill="none"
        stroke={color}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      {leaves([
        [410, 287, 40, 1.0],
        [390, 277, 38, 1.05],
        [368, 266, 36, 1.1],
        [346, 255, 34, 1.1],
        [324, 245, 32, 1.05],
        [302, 236, 30, 1.0],
        [280, 230, 28, 0.95],
        [400, 297, -38, 1.0],
        [378, 287, -36, 1.05],
        [356, 276, -34, 1.1],
        [334, 265, -32, 1.05],
        [312, 256, -30, 1.0],
        [290, 248, -28, 0.95],
      ])}
      <circle cx="420" cy="297" r="2.8" fill={color} />
      <circle cx="406" cy="291" r="2.8" fill={color} />
      <circle cx="390" cy="283" r="2.8" fill={color} />
      <circle cx="370" cy="272" r="2.8" fill={color} />
    </svg>
  );
};

// Animated text helpers: stagger reveal by letter (block fonts) or by word (script)
const AnimatedLetters = ({ text, base = 0.4, step = 0.05, className = "", style = {} }) => (
  <span className={`stagger-letters ${className}`} style={style} aria-label={text}>
    {Array.from(text).map((ch, i) => {
      const isSpace = ch === " ";
      return (
        <span
          key={i}
          className="letter"
          data-space={isSpace ? "true" : "false"}
          style={{ animationDelay: `${base + i * step}s` }}
        >
          {isSpace ? "\u00A0" : ch}
        </span>
      );
    })}
  </span>
);

const AnimatedWords = ({ text, base = 0.4, step = 0.18, className = "", style = {} }) => (
  <span className={`stagger-script ${className}`} style={style} aria-label={text}>
    {text.split(" ").map((w, i) => (
      <span key={i} className="word" style={{ animationDelay: `${base + i * step}s` }}>
        {w}
        {i < text.split(" ").length - 1 ? "\u00A0" : ""}
      </span>
    ))}
  </span>
);

// PhotoLetterRow — 3 photos behind, big letters in CARD COLOR on top so the
// photos are visible in the negative space AROUND/BETWEEN the letters.
const PhotoLetterRow = ({ word, photos, idPrefix, base = 0.2, step = 0.18 }) => {
  const n = photos.length;
  const cellW = 100 / n;
  return (
    <div className="w-full reveal-soft" style={{ animationDelay: `${base}s` }}>
      <svg
        viewBox="0 0 100 30"
        preserveAspectRatio="xMidYMid meet"
        className="w-full block"
        aria-label={word}
      >
        <defs>
          <mask id={`${idPrefix}-mask`} maskUnits="userSpaceOnUse">
            {/* white = visible, black = hidden (here black = letter shape blocks the photo) */}
            <rect width="100" height="30" fill="white" />
            <text
              x="50"
              y="25"
              textAnchor="middle"
              fontFamily="Anton, Impact, sans-serif"
              fontWeight="400"
              fontSize="33"
              letterSpacing="-0.6"
              fill="black"
            >
              {word}
            </text>
          </mask>
        </defs>
        {/* Photo strip behind, masked so letter shapes are cut out */}
        <g mask={`url(#${idPrefix}-mask)`}>
          {photos.map((src, i) => (
            <image
              key={i}
              href={src}
              x={i * cellW}
              y={0}
              width={cellW}
              height={30}
              preserveAspectRatio="xMidYMid slice"
              style={{
                opacity: 0,
                animation: `fadeIn 0.9s ease-out ${base + i * step}s forwards`,
              }}
            />
          ))}
        </g>
      </svg>
    </div>
  );
};

// Decorative twin-leaf divider matching the reference's olive sprig
const TwinLeaves = ({ className = "", color = "#5b6b48" }) => (  <svg viewBox="0 0 400 60" className={className} aria-hidden="true">
    {/* Left branch */}
    <g fill={color} stroke={color} strokeWidth="0.8">
      <path d="M30 30 Q 100 30, 175 30" fill="none" />
      {[
        [50, 22, -20], [70, 36, 20], [88, 20, -22], [108, 38, 22],
        [128, 22, -22], [148, 38, 22], [168, 28, -10],
      ].map(([x, y, rot], i) => (
        <ellipse key={`tl${i}`} cx={x} cy={y} rx="8" ry="2" transform={`rotate(${rot} ${x} ${y})`} />
      ))}
    </g>
    {/* Center heart */}
    <path
      d="M200 28 c -5 -8 -14 -8 -14 0 c 0 6 14 12 14 12 c 0 0 14 -6 14 -12 c 0 -8 -9 -8 -14 0 z"
      fill={color}
    />
    {/* Right branch */}
    <g fill={color} stroke={color} strokeWidth="0.8">
      <path d="M225 30 Q 300 30, 370 30" fill="none" />
      {[
        [232, 28, 10], [252, 22, 22], [272, 38, -22], [292, 22, 22],
        [312, 38, -22], [332, 22, 20], [352, 36, -20],
      ].map(([x, y, rot], i) => (
        <ellipse key={`tr${i}`} cx={x} cy={y} rx="8" ry="2" transform={`rotate(${rot} ${x} ${y})`} />
      ))}
    </g>
  </svg>
);

const Divider = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-3 ${className}`}>
    <span className="h-px w-12 bg-current opacity-60" />
    <OliveBranch className="w-12 h-4 opacity-80" />
    <span className="h-px w-12 bg-current opacity-60" />
  </div>
);

export default function SaveTheDate() {
  const { days, hours, minutes, seconds } = useCountdown(EVENT.dateISO);
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("std_lang") || "es";
    } catch {
      return "es";
    }
  });
  const t = I18N[lang];
  const toggleLang = () => {
    const next = lang === "es" ? "en" : "es";
    setLang(next);
    try {
      localStorage.setItem("std_lang", next);
    } catch {
      /* ignore */
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t.toastCopied, { description: t.toastCopiedDesc });
    } catch {
      toast.error(t.toastError);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: t.shareTitle,
      text: `${t.phrase} · ${EVENT.prettyDate} · ${EVENT.city}`,
      url: window.location.href,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user cancelled */
      }
    } else {
      const wa = `https://wa.me/?text=${encodeURIComponent(
        `${shareData.text}\n${shareData.url}`,
      )}`;
      window.open(wa, "_blank");
    }
  };

  return (
    <main
      data-testid="save-the-date-page"
      className="relative min-h-screen w-full overflow-hidden text-[#3a2e22]"
    >
      {/* HERO — vertical portrait editorial layout — Dallas translucent backdrop */}
      <section className="relative w-full bg-[#f6efe4]">
        <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[640px] flex-col items-center px-3 pb-10 pt-6 sm:px-6 sm:pt-10">
          {/* Language toggle */}
          <div className="mb-4 flex items-center justify-center">
            <div
              className="flex items-center gap-1 rounded-full bg-[#f3e9d6]/85 backdrop-blur-sm px-1 py-1 shadow-vintage"
              data-testid="lang-toggle"
            >
              {[
                { code: "es", label: "ES" },
                { code: "en", label: "EN" },
              ].map((opt) => (
                <button
                  key={opt.code}
                  type="button"
                  data-testid={`lang-${opt.code}`}
                  onClick={() => {
                    if (lang !== opt.code) toggleLang();
                  }}
                  className={`font-display tracking-[0.2em] text-[11px] px-3 py-1 rounded-full transition-colors ${
                    lang === opt.code
                      ? "bg-[#5b6b48] text-[#f3e9d6]"
                      : "text-[#5b6b48] hover:bg-[#5b6b48]/10"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <article
            data-testid="hero-card"
            className="relative w-full overflow-hidden rounded-[3px] bg-[#f8f2e6] shadow-vintage"
            style={{ maxWidth: "560px" }}
          >
            {/* Dallas skyline backdrop — prominent, Reunion Tower anchored on left */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-0 h-[72%] overflow-hidden">
              <img
                src={DALLAS_BG}
                alt=""
                aria-hidden="true"
                className="h-full w-full object-cover"
                style={{ opacity: 0.62, filter: "saturate(0.55) brightness(1.05) contrast(0.92)", objectPosition: "15% 100%" }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-[#f8f2e6] via-[#f8f2e6]/10 to-[#f8f2e6]/30" />
            </div>

            {/* Content stack */}
            <div className="relative z-10 px-5 pt-8 pb-8 sm:px-8 sm:pt-12 sm:pb-12">

              {/* TESSELLATED DIAMOND COLLAGE (4 diamonds forming a bigger diamond) */}
              <div
                className="relative mx-auto reveal-soft"
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  aspectRatio: "1 / 1",
                  animationDelay: "0.1s",
                }}
                data-testid="hero-image"
              >
                {/* Each diamond is a 50%x50% absolute square clipped to a rhombus.
                    Adjacent diamonds touch at their tips, forming a bigger diamond. */}

                {/* TOP — ring hand */}
                <div
                  className="absolute overflow-hidden"
                  style={{
                    left: "25%",
                    top: "0%",
                    width: "50%",
                    height: "50%",
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                  }}
                >
                  <img
                    src={RING_HAND}
                    alt=""
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "center 45%" }}
                  />
                </div>

                {/* LEFT — couple original photo */}
                <div
                  className="absolute overflow-hidden"
                  style={{
                    left: "0%",
                    top: "25%",
                    width: "50%",
                    height: "50%",
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                  }}
                >
                  <img
                    src={COUPLE_PHOTO}
                    alt="Bricia y Luis"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "center 30%" }}
                  />
                </div>

                {/* RIGHT — kissing couple at door */}
                <div
                  className="absolute overflow-hidden"
                  style={{
                    left: "50%",
                    top: "25%",
                    width: "50%",
                    height: "50%",
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                  }}
                >
                  <img
                    src={COUPLE_KISS_DOOR}
                    alt="Bricia y Luis"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "center 30%" }}
                  />
                </div>

                {/* BOTTOM — couple seated in park */}
                <div
                  className="absolute overflow-hidden"
                  style={{
                    left: "25%",
                    top: "50%",
                    width: "50%",
                    height: "50%",
                    clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
                  }}
                >
                  <img
                    src={COUPLE_PHOTO_SEATED}
                    alt="Bricia y Luis"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: "center 32%" }}
                  />
                </div>
              </div>

              {/* DATE STACK — 21 / MAYO / 2027 with thin dividers */}
              <div
                className="reveal-soft mt-8 text-center"
                style={{ animationDelay: "0.9s" }}
                key={`dt-${lang}`}
              >
                <p className="font-display tracking-[0.08em] text-[34px] sm:text-[40px] leading-none text-[#2d2316]">
                  21
                </p>
                <div className="my-2 mx-auto h-px w-10 bg-[#2d2316]" />
                <p className="font-display tracking-[0.32em] text-[13px] sm:text-[14px] text-[#2d2316]">
                  {t.month}
                </p>
                <div className="my-2 mx-auto h-px w-10 bg-[#2d2316]" />
                <p className="font-display tracking-[0.12em] text-[22px] sm:text-[26px] text-[#2d2316]">
                  2027
                </p>
              </div>

              {/* SAVE THE DATE — elegant copperplate script with —THE— divider */}
              <div
                data-testid="hero-eyebrow"
                className="mt-5 text-center reveal-soft"
                style={{ animationDelay: "1.1s" }}
              >
                <p
                  className="leading-[0.9] text-[#2d2316]"
                  style={{
                    fontFamily: "'Pinyon Script', 'Allura', cursive",
                    fontSize: "clamp(78px, 21vw, 120px)",
                  }}
                >
                  Save
                </p>
                <div className="my-1 flex items-center justify-center gap-3">
                  <span className="h-px w-10 bg-[#2d2316]" />
                  <span
                    className="font-display tracking-[0.36em] text-[14px] sm:text-[15px] text-[#2d2316]"
                  >
                    THE
                  </span>
                  <span className="h-px w-10 bg-[#2d2316]" />
                </div>
                <p
                  className="leading-[0.9] text-[#2d2316]"
                  style={{
                    fontFamily: "'Pinyon Script', 'Allura', cursive",
                    fontSize: "clamp(78px, 21vw, 120px)",
                  }}
                >
                  Date
                </p>
              </div>

              {/* B & L Monogram — larger */}
              <div
                className="mt-3 flex justify-center reveal-soft"
                style={{ animationDelay: "1.35s" }}
              >
                <img
                  src={BL_LOGO}
                  alt="B & L monogram"
                  data-testid="bl-logo"
                  className="h-32 w-32 sm:h-36 sm:w-36 object-contain"
                />
              </div>

              {/* Names */}
              <h1
                data-testid="hero-names"
                className="font-display mt-6 text-center tracking-[0.20em] text-[20px] sm:text-[24px] leading-[1.35] reveal-soft text-[#2d2316]"
                style={{ animationDelay: "1.55s" }}
              >
                BRICIA GARCÍA
                <br />
                &amp; LUIS GARCÍA
              </h1>

              <TwinLeaves
                className="mx-auto mt-5 h-5 w-44 reveal-soft"
                color="#5b6b48"
                style={{ animationDelay: "1.7s" }}
              />

              {/* Formal invitation to follow */}
              <p
                className="mt-3 text-center italic text-[14px] sm:text-[15px] text-[#6d5a3a] reveal-soft"
                style={{ fontFamily: "'Cormorant Garamond', serif", animationDelay: "1.85s" }}
              >
                {lang === "es" ? "Invitación formal a seguir" : "formal invitation to follow"}
              </p>

              {/* Tiny heart */}
              <div
                className="mt-2 flex justify-center reveal-soft"
                style={{ animationDelay: "2.0s" }}
              >
                <Heart className="h-3 w-3 text-[#5b6b48]" fill="#5b6b48" />
              </div>

              {/* Hidden hero-location for tests (address moved to details card below) */}
              <div data-testid="hero-location" className="sr-only">
                <p>3747 GRACE RANCH TRL</p>
                <p>MCKINNEY, TEXAS 75071</p>
              </div>

              {/* Hidden hero-date for tests */}
              <p data-testid="hero-date" className="sr-only">
                {t.month} 21 2027
              </p>

              {/* Hidden phrase for tests */}
              <p
                data-testid="hero-phrase"
                key={`ph-${lang}`}
                className="sr-only"
              >
                {t.phrase}
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* DETAILS / CTA CARD */}
      <section className="relative z-10 mx-auto max-w-3xl px-4 pt-10 pb-16 sm:px-6">
        <aside data-testid="details-card">
            <div className="relative h-full rounded-[2px] paper-texture p-7 shadow-vintage sm:p-10 grain-overlay">
              <div className="text-center">
                <p className="tracking-extra text-[10px] uppercase text-[#5b6b48]">
                  {t.destinationLove}
                </p>
                <TwinLeaves className="mx-auto mt-2 h-6 w-44" color="#5b6b48" />

                <p
                  data-testid="details-date"
                  className="font-display mt-5 text-3xl sm:text-4xl tracking-[0.12em] text-[#2d2316]"
                >
                  {EVENT.prettyDate}
                </p>
                <p className="font-serif-elegant mt-1 text-base italic text-[#6d5a3a]">
                  {t.longDate}
                </p>

                <div className="mt-6 flex items-center justify-center gap-2 text-[#3a2e22]">
                  <MapPin className="h-4 w-4 text-[#7a6342]" />
                  <p
                    data-testid="details-address"
                    className="text-sm leading-relaxed"
                  >
                    McKinney, Texas {t.cityArea}
                  </p>
                </div>

                {/* Countdown */}
                <div className="mt-7" data-testid="countdown">
                  <p className="tracking-extra text-[10px] uppercase text-[#7a6342]">
                    {t.countdownLabel}
                  </p>
                  <div className="mt-3 grid grid-cols-4 gap-2 text-[#3a2e22]">
                    {[
                      { v: days, l: t.units.d, k: "days" },
                      { v: hours, l: t.units.h, k: "hrs" },
                      { v: minutes, l: t.units.m, k: "min" },
                      { v: seconds, l: t.units.s, k: "seg" },
                    ].map((u) => (
                      <div
                        key={u.k}
                        className="rounded-[2px] border border-[#c9b88f]/60 bg-[#fbf3e1]/70 px-2 py-3"
                      >
                        <div
                          data-testid={`countdown-${u.k}`}
                          className="font-display text-2xl sm:text-3xl leading-none"
                        >
                          {String(u.v).padStart(2, "0")}
                        </div>
                        <div className="mt-1 tracking-extra-wide text-[9px] uppercase text-[#7a6342]">
                          {u.l}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-7 flex flex-col items-stretch gap-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="add-to-calendar-btn"
                        className="group h-12 w-full rounded-full bg-[#3a2e22] text-[#f3e9d6] hover:bg-[#52402d]"
                      >
                        <CalendarPlus className="mr-2 h-4 w-4" />
                        {t.addToCal}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="center"
                      className="w-64 bg-[#fbf3e1] border-[#c9b88f]"
                      data-testid="add-to-calendar-menu"
                    >
                      <DropdownMenuItem
                        data-testid="cal-google"
                        onClick={() => window.open(googleCalUrl(), "_blank")}
                        className="cursor-pointer text-[#3a2e22]"
                      >
                        Google Calendar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="cal-apple"
                        onClick={downloadICS}
                        className="cursor-pointer text-[#3a2e22]"
                      >
                        Apple Calendar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      data-testid="copy-link-btn"
                      onClick={handleCopyLink}
                      variant="outline"
                      className="h-11 rounded-full border-[#3a2e22]/30 bg-transparent text-[#3a2e22] hover:bg-[#3a2e22]/5"
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      {t.copyLink}
                    </Button>
                    <Button
                      data-testid="share-btn"
                      onClick={handleShare}
                      variant="outline"
                      className="h-11 rounded-full border-[#3a2e22]/30 bg-transparent text-[#3a2e22] hover:bg-[#3a2e22]/5"
                    >
                      <Share2 className="mr-2 h-4 w-4" />
                      {t.share}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </aside>
      </section>
    </main>
  );
}
